import { isTelegram } from '../detector';

/**
 * Show an alert dialog
 * Uses Telegram's showAlert in Telegram mode, native alert in standalone
 * @param {string} message - Message to display
 * @param {Function} callback - Optional callback after alert is dismissed
 */
export function showAlert(message, callback) {
  if (isTelegram) {
    window.Telegram.WebApp.showAlert(message, callback);
  } else {
    // Use native alert for now
    // TODO: Replace with custom modal component for better UX
    alert(message);
    if (callback) callback();
  }
}

/**
 * Show a confirmation popup
 * @param {Object} options - Popup options
 * @param {string} options.title - Popup title (optional)
 * @param {string} options.message - Popup message
 * @param {Array} options.buttons - Button configurations
 * @param {Function} callback - Callback with selected button id
 */
export function showPopup(options, callback) {
  if (isTelegram) {
    window.Telegram.WebApp.showPopup(options, callback);
  } else {
    // Simple confirm for standalone
    // TODO: Replace with custom modal component
    const confirmed = window.confirm(options.message);
    if (callback) {
      // Find the OK/confirm button or first button
      const okButton = options.buttons?.find(b => b.type === 'ok' || b.type === 'default');
      const cancelButton = options.buttons?.find(b => b.type === 'cancel' || b.type === 'close');

      if (confirmed && okButton) {
        callback(okButton.id);
      } else if (!confirmed && cancelButton) {
        callback(cancelButton.id);
      } else if (callback) {
        callback(confirmed ? 'ok' : 'cancel');
      }
    }
  }
}

/**
 * Show a confirmation dialog (simplified version of showPopup)
 * @param {string} message - Confirmation message
 * @param {Function} onConfirm - Called if user confirms
 * @param {Function} onCancel - Called if user cancels (optional)
 */
export function showConfirm(message, onConfirm, onCancel) {
  showPopup({
    message,
    buttons: [
      { id: 'confirm', type: 'ok', text: 'OK' },
      { id: 'cancel', type: 'cancel', text: 'Cancel' }
    ]
  }, (buttonId) => {
    if (buttonId === 'confirm') {
      onConfirm?.();
    } else {
      onCancel?.();
    }
  });
}

/**
 * Open invoice for payment (Telegram only)
 * @param {string} invoiceUrl - Invoice URL
 * @param {Function} callback - Callback with payment status
 */
export function openInvoice(invoiceUrl, callback) {
  if (isTelegram) {
    window.Telegram.WebApp.openInvoice(invoiceUrl, callback);
  } else {
    // For standalone, open invoice URL in new tab
    window.open(invoiceUrl, '_blank');
    if (callback) callback('cancelled'); // Can't track payment in standalone
  }
}

/**
 * Open a Telegram link
 * @param {string} url - Telegram URL (t.me/...)
 */
export function openTelegramLink(url) {
  if (isTelegram) {
    window.Telegram.WebApp.openTelegramLink(url);
  } else {
    window.open(url, '_blank');
  }
}

/**
 * Open an external link
 * @param {string} url - URL to open
 * @param {Object} options - Options (e.g., { try_instant_view: true })
 */
export function openLink(url, options = {}) {
  if (isTelegram) {
    window.Telegram.WebApp.openLink(url, options);
  } else {
    window.open(url, '_blank');
  }
}

/**
 * Close the webapp
 */
export function closeWebApp() {
  if (isTelegram) {
    window.Telegram.WebApp.close();
  } else {
    // In standalone, we can't close the tab programmatically
    // Navigate to home or show a message
    window.location.href = '/';
  }
}
